/* eslint-disable react/prop-types */
import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Button,
  Drawer,
  Input,
  Typography,
  Spin,
  Alert,
  Tag,
  Empty,
  Switch,
} from 'antd';
import {
  RobotOutlined,
  SendOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import { postChatAsistenteIA } from 'api/asistenteIA/chat';
import { hasActionPermission } from 'utils/permission';
import { selectUser } from 'store/auth';

// Prototipo: restringido únicamente a este usuario mientras se valida
// con datos reales, para que otros superusuarios no lo vean todavía.
const USUARIO_ID_PROTOTIPO = 12;

const { Text } = Typography;
const { TextArea } = Input;

/**
 * Construye el texto que se envía al backend, anteponiendo el contexto
 * de nómina/empleado ya conocido por la pantalla para que el asistente
 * no tenga que pedírselo al usuario.
 */
const construirMensajeConContexto = (mensaje, contexto, incluirEmpleado) => {
  const pistas = [];
  if (contexto?.nominaId) {
    pistas.push(`nomina_id=${contexto.nominaId}`);
    if (contexto.numeroNomina) {
      // Rotulado explícito como "folio_nomina" (no "numero_de_empleado")
      // para que el asistente nunca confunda el folio de la nómina con
      // el número de un empleado.
      pistas.push(`folio_nomina=${contexto.numeroNomina}`);
    }
  }
  // El switch "Incluir empleado" permite al usuario preguntar sobre la
  // nómina en general sin tener que deseleccionar la fila en la tabla de
  // fondo: si está apagado, se envía como si no hubiera empleado elegido.
  if (incluirEmpleado && contexto?.empleadoNumero) {
    pistas.push(`numero_de_empleado=${contexto.empleadoNumero}`);
  } else {
    pistas.push('numero_de_empleado=(ninguno seleccionado)');
  }
  if (!pistas.length) {
    return mensaje;
  }
  return `[Contexto de la pantalla: ${pistas.join(', ')}] ${mensaje}`;
};

/**
 * Widget flotante de Asistente de IA para consultas de nómina.
 *
 * Es un chat de una sola vuelta por mensaje (el backend no mantiene
 * memoria entre requests): cada pregunta se envía junto con el contexto
 * de nómina/empleado visible en la pantalla, y la respuesta siempre
 * proviene de datos reales ya calculados (nunca de una suposición del LLM).
 */
const AsistenteChatWidget = ({ contexto }) => {
  const [visible, setVisible] = useState(false);
  const [mensajes, setMensajes] = useState([]);
  const [entrada, setEntrada] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  // Controla si la pregunta se acota al empleado seleccionado en la
  // tabla de fondo o solo a la nómina en general. Permite alternar sin
  // tener que cerrar el chat ni deseleccionar la fila.
  const [incluirEmpleado, setIncluirEmpleado] = useState(true);
  const listRef = useRef(null);
  const textAreaRef = useRef(null);
  const siguienteIdRef = useRef(0);

  const user = useSelector(selectUser);
  const puedeVerAsistente = hasActionPermission('nomina', 'asistenteia')
    && user?.id === USUARIO_ID_PROTOTIPO;

  // Cuando cambia el empleado seleccionado en la pantalla (uno nuevo, o
  // ninguno), el switch vuelve a su estado por defecto (incluido).
  useEffect(() => {
    setIncluirEmpleado(true);
  }, [contexto?.empleadoNumero]);

  const agregarMensaje = (rol, texto) => {
    siguienteIdRef.current += 1;
    setMensajes((prev) => [...prev, { id: siguienteIdRef.current, rol, texto }]);
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [mensajes, enviando]);

  // Devuelve el foco al input en cuanto el asistente termina de responder
  // (o al abrir el panel), para que el usuario pueda seguir escribiendo sin
  // tener que volver a hacer clic en el textarea.
  useEffect(() => {
    if (visible && !enviando) {
      textAreaRef.current?.focus();
    }
  }, [enviando, visible]);

  const onEnviar = async () => {
    const mensaje = entrada.trim();
    if (!mensaje || enviando) {
      return;
    }
    setError('');
    agregarMensaje('usuario', mensaje);
    setEntrada('');
    setEnviando(true);

    const payload = {
      mensaje: construirMensajeConContexto(mensaje, contexto, incluirEmpleado),
      ...(incluirEmpleado && contexto?.empleadoId ? { empleado_id: contexto.empleadoId } : {}),
    };

    const respuesta = await postChatAsistenteIA(payload);

    if (respuesta && respuesta.respuesta) {
      agregarMensaje('asistente', respuesta.respuesta);
    } else if (respuesta === false) {
      setError('No se pudo obtener respuesta del asistente. Intenta de nuevo en unos momentos.');
    } else {
      setError('El asistente no devolvió una respuesta.');
    }
    setEnviando(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      onEnviar();
    }
  };

  if (!puedeVerAsistente) {
    return null;
  }

  return (
    <>
      <Button
        type="primary"
        shape="circle"
        icon={<RobotOutlined />}
        size="large"
        onClick={() => setVisible(true)}
        style={{
          position: 'fixed',
          bottom: 32,
          right: 32,
          width: 56,
          height: 56,
          // Por encima de cualquier Modal/Drawer de Ant Design (z-index base
          // 1000, incrementado en +2 por cada instancia anidada), para que
          // el botón nunca quede tapado al abrir "Detalle Recibo" u otros
          // diálogos de la app.
          zIndex: 100000,
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.35)',
        }}
        title="Asistente de Nómina (IA)"
      />
      <Drawer
        title={(
          <>
            <RobotOutlined style={{ marginRight: 8 }} />
            Asistente de Nómina (IA)
          </>
        )}
        placement="right"
        width={420}
        open={visible}
        onClose={() => setVisible(false)}
        // Mismo motivo que el botón flotante: debe quedar por encima de
        // cualquier otro Modal/Drawer ya abierto en la pantalla.
        zIndex={100000}
        bodyStyle={{
          display: 'flex', flexDirection: 'column', padding: 12, height: '100%',
        }}
      >
        <Alert
          type="info"
          showIcon
          message="Prototipo"
          description="Responde únicamente con datos reales ya calculados en el sistema (nunca inventa montos). Cada pregunta se responde de forma independiente."
          style={{ marginBottom: 12 }}
        />
        {(contexto?.nominaId || contexto?.empleadoNumero) && (
          <div
            style={{
              marginBottom: 12, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8,
            }}
          >
            {contexto?.nominaId && (
              <Tag color="blue">
                Nómina:
                {' '}
                {[
                  contexto.numeroNomina || contexto.nominaId,
                  contexto.tipoNominaEmpleadoDescripcion,
                  contexto.periodoDetalleTexto,
                ].filter(Boolean).join(' ')}
              </Tag>
            )}
            {contexto?.empleadoNumero && (
              <>
                <Tag color={incluirEmpleado ? 'green' : 'default'}>
                  Empleado:
                  {' '}
                  {contexto.empleadoNumeroYNombre || contexto.empleadoNumero}
                </Tag>
                <span
                  style={{ display: 'flex', alignItems: 'center', gap: 6 }}
                  title="Actívalo para acotar las preguntas al empleado seleccionado; apágalo para preguntar sobre la nómina en general sin perder la selección de la tabla."
                >
                  <Switch
                    size="small"
                    checked={incluirEmpleado}
                    onChange={setIncluirEmpleado}
                  />
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {incluirEmpleado ? 'Incluir Empleado' : 'Solo Nómina'}
                  </Text>
                </span>
              </>
            )}
          </div>
        )}

        <div
          ref={listRef}
          style={{
            flex: 1,
            overflowY: 'auto',
            marginBottom: 12,
            paddingRight: 4,
          }}
        >
          {mensajes.length === 0 ? (
            <Empty
              description="Pregunta, por ejemplo: ¿Cuál es el total de percepciones de la nómina seleccionada?"
              style={{ marginTop: 40 }}
            />
          ) : (
            mensajes.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  justifyContent: m.rol === 'usuario' ? 'flex-end' : 'flex-start',
                  marginBottom: 8,
                }}
              >
                <div
                  style={{
                    maxWidth: '85%',
                    padding: '8px 12px',
                    borderRadius: 8,
                    whiteSpace: 'pre-wrap',
                    backgroundColor: m.rol === 'usuario' ? '#1890ff' : '#f0f2f5',
                    color: m.rol === 'usuario' ? '#fff' : 'inherit',
                  }}
                >
                  <Text style={{ color: m.rol === 'usuario' ? '#fff' : 'inherit' }}>
                    {m.texto}
                  </Text>
                </div>
              </div>
            ))
          )}
          {enviando && (
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 8 }}>
              <Spin size="small" style={{ marginRight: 8 }} />
              <Text type="secondary">El asistente está consultando los datos...</Text>
            </div>
          )}
        </div>

        {error && (
          <Alert
            type="error"
            showIcon
            closable
            onClose={() => setError('')}
            icon={<CloseCircleOutlined />}
            message={error}
            style={{ marginBottom: 12 }}
          />
        )}

        <div style={{ display: 'flex', gap: 8 }}>
          <TextArea
            ref={textAreaRef}
            value={entrada}
            onChange={(e) => setEntrada(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Escribe tu pregunta sobre la nómina..."
            autoSize={{ minRows: 1, maxRows: 4 }}
            disabled={enviando}
          />
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={onEnviar}
            disabled={enviando || !entrada.trim()}
            loading={enviando}
          />
        </div>
      </Drawer>
    </>
  );
};

export default AsistenteChatWidget;
